/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {ChangeEvent, useEffect, useRef, useState} from 'react';
import {makeStyles} from '@material-ui/core';
import FormControl from '@material-ui/core/FormControl';
import Autocomplete from '@material-ui/lab/Autocomplete';
import {translate} from '@ion/core/src/locale/locale';
import {
  CTCEntity
} from '@ion/core/src/types/entities';
import {FormTextField} from '@ion/dls/src/components/forms/FormTextField';
import {Options} from '@ion/dls/src/components/menus/Dropdown';
import {dlsAlt, dlsColor, dlsZIndex} from '@ion/dls/src/theme/theme';
import {
  getOptionLabel,
  getOptions,
  getOptionSelected,
  onDropDownChange
} from '@ion/features/src/helpers';

// defines the Roles chooser prop definitions ...
export type RoleChooserProps = {
  onChange?: (roles: CTCEntity, key: number) => void;
  readOnly?: boolean;
  required?: boolean;
  validationError?: string;
  classes?: string;
  defaultValue?: string;
  roles: any;
  loading: boolean;
  keyObj: any;
  focusRoleField: boolean;
}

// defines the styles used by Roles chooser ...
const noPadAndMargin = {
  padding : '0px !important',
  margin  : '0px !important'
};
export const useStyles = makeStyles(theme => ({
  root : {
    ...noPadAndMargin
  },
  listbox : {
    ...noPadAndMargin
  },
  popper : {
    zIndex : dlsZIndex('roleChooserPopper')
  },
  paper : {
    padding : '0px !important'
  },
  option : {
    padding       : '17px !important',
    ...dlsAlt('components', 'dropdown'),
    color         : dlsColor('grey3'),
    textTransform : ({upperCase}: {upperCase: boolean}) => (
      upperCase ? 'uppercase' : 'none'
    ),
    letterSpacing : ({upperCase}: {upperCase: boolean}) => (
      upperCase ? dlsAlt('components', 'dropdown').letterSpacing : 'normal'
    ),
    '&[data-focus="true"]' : {
      backgroundColor : `${dlsAlt('components', 'icon', 'selected')} !important`
    }
  },
  endAdornment : {
    paddingRight : '7px !important'
  },
  input : {
    paddingLeft  : '7px !important',
    paddingRight : '3em !important'
  },
  inputRoot : {
    '&::before' : {
      borderBottom : 'none !important'
    },
    '&.Mui-focused' : {
      '&::before' : {
        borderBottom : `1px solid ${dlsColor('aaBlue')} !important`
      }
    }
  },
  formControl : {
    borderBottom           : ({hasError}: {hasError: boolean;}) => hasError ? 'none' : `1px solid ${dlsColor('grey5')}`,
    '& .MuiInputBase-root' : {
      width        : '100%',
      marginTop    : 0,
      border       : ({hasError}: {hasError: boolean;}) => hasError ? `solid 2px ${dlsColor('caRed')}` : 'none',
      borderBottom : 'none !important'
    }
  },
  popupIndicator : {
    '&:hover, &:active' : {
      backgroundColor : 'transparent'
    },
    '& .MuiTouchRipple-root' : {
      display : 'none'
    }
  },
  clearIndicator : {
    '&:hover, &:active' : {
      backgroundColor : 'transparent'
    },
    '& .MuiTouchRipple-root' : {
      display : 'none'
    },
    '& .MuiSvgIcon-root' : {
      width     : '0.65em',
      height    : '0.65em',
      '&:hover' : {
        color : dlsColor('caRed')
      }
    }
  }
}));

/**
 *
 * The RoleChooser provides a list of Roles for the user's default account
 * for display within a dropdown list and allows the user to search and select an Roles
 * from the list which will be returned to the client as the selected Roles.
 *
 *     // read-only mode ...
 *     <RoleChooser readOnly
 *       />
 *
 *     // with default value and required check ...
 *     <RoleChooser required
 *       />
 *
 *     // with callback subscription ...
 *     <RoleChooser onChange={setroles}
 *       />
 *
 * @onChange         Callback prop which will be invoked with the seleted Roles.
 * @readOnly         When true, the component will be displayed in read-only mode;
 *                   that is, disabled. Defaults to false.
 * @required         When true, the required indicator will be displayed.
 * @validationError  When true, the validationError indicator will be displayed.
 * @classes          Optional classes to apply to the wrapping element.
 * @defaultValue     Default Value of the field
 */
export const RoleChooser = ({
  onChange,
  readOnly,
  required,
  classes,
  validationError,
  defaultValue,
  roles,
  loading,
  keyObj,
  focusRoleField
}: RoleChooserProps) => {
  const hasError                              = Boolean(validationError);
  const _classes                              = useStyles({hasError});
  const [open, setOpen]                       = useState(false);
  const [selected, setSelected]               = useState<string>(defaultValue);
  const [options, setOptions]                 = useState<Options[]>([]);
  const inputRef                              = useRef(null);

  // manages loading Roles based on current search term ...
  function loadRoles() {
    setOptions(getOptions(roles));
  }

  useEffect(() => {
    setSelected(defaultValue);
    if (focusRoleField) {
      inputRef.current?.focus();
    }
  }, [defaultValue]);

  useEffect(() => {
    roles?.length > 0 ? loadRoles() : (setOptions([]),
    setSelected(''));
  }, [roles, selected]);

  // invoked when a textfield change is made, delegating the debounce change handler ...
  function handleFieldChange(e: ChangeEvent<HTMLInputElement>) {
    if (e) {
      e?.persist();
    }
  };

  return (
    <div className={classes || _classes.root}>
      <FormControl className={_classes.formControl}>
        <Autocomplete disabled={readOnly}
          defaultValue={defaultValue || ''}
          value={selected || ''}
          autoComplete={false}
          clearOnBlur={false}
          openText=''
          closeText=''
          clearText={translate('common:features.roles.clearText')}
          classes={{
            popper         : _classes.popper,
            paper          : _classes.paper,
            popupIndicator : _classes.popupIndicator,
            clearIndicator : _classes.clearIndicator,
            endAdornment   : _classes.endAdornment,
            input          : _classes.input,
            inputRoot      : _classes.inputRoot,
            listbox        : _classes.listbox,
            option         : _classes.option
          }}
          open={open}
          onOpen={function() {
            setOpen(true);
          }}
          onClose={function() {
            setOpen(false);
          }}
          getOptionSelected={getOptionSelected}
          getOptionLabel={getOptionLabel}
          onChange={
            function(e, value) {
              const params = {
                setSelected, listObj : roles, keyObj
              };
              return onDropDownChange(onChange, value, params);
            }}
          options={options}
          selectOnFocus={false}
          openOnFocus
          clearOnEscape
          loading={loading}
          loadingText={translate('common:states.loading')}
          noOptionsText={translate('common:features.roles.noOptionsText')}
          onInputChange={handleFieldChange}
          renderInput={function(params) {
            return (
              <FormTextField {...params}
                autoComplete='off'
                inputRef={inputRef}
                error={(validationError !== '')}
                helperText={validationError}
                required={required}
                fieldLabel={translate('common:features.roles.label')}
                placeholder={translate('common:features.roles.placeholder')}
                name={translate('common:features.roles.name')}
                InputProps={{
                  ...params.InputProps
                }} />
            );
          }} />
      </FormControl>
    </div>
  );
};

export default RoleChooser;
